import { useMemo } from 'react'

import { useAuthStore } from '@/stores/auth'

import type { NavItem } from '@/types/nav-item'

/**
 * Filter navigation items based on user permissions
 *
 * Uses OR logic:
 * - If requiredPermissions is set, user needs at least ONE permission
 * - If requiredRoles is set (fallback), user needs at least ONE role
 * - If neither is set, item is visible to all
 *
 * Recursively filters nested items.
 */
export function usePermissionFilteredNav(navItems: NavItem[]): NavItem[] {
  const { user } = useAuthStore()

  return useMemo(() => {
    if (!user) return []

    const userPermissions = user.permissions ?? []
    const userRoles = user.roles ?? []

    const filterItems = (items: NavItem[]): NavItem[] => {
      return items
        .map((item) => {
          // Check permissions (OR logic - user needs at least one)
          if (item.requiredPermissions && item.requiredPermissions.length > 0) {
            const hasPermission = item.requiredPermissions.some((perm) => userPermissions.includes(perm))
            if (!hasPermission) return null
          }

          // Check roles if no permissions specified (fallback)
          if (!item.requiredPermissions && item.requiredRoles && item.requiredRoles.length > 0) {
            const hasRole = item.requiredRoles.some((role) => userRoles.includes(role as any))
            if (!hasRole) return null
          }

          // Filter nested items recursively
          if (item.items && item.items.length > 0) {
            const filteredSubItems = filterItems(item.items)

            // If parent has no visible children, hide parent unless it has its own route
            if (filteredSubItems.length === 0 && item.items.length > 0) {
              return null
            }

            return {
              ...item,
              items: filteredSubItems,
            }
          }

          return item
        })
        .filter((item): item is NavItem => item !== null)
    }

    return filterItems(navItems)
  }, [user, navItems])
}
